import { useMutation } from "@apollo/client/react";
import { DSButton } from "@components/design-system/molecules";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DISMISS_NOTIFICATION } from "@graphql/mutations/DismissNotification";
import { MARK_NOTIFICATION_READ } from "@graphql/mutations/MarkNotificationRead";
import { useVisibleNotifications } from "@hooks";
import { useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router";

import NotificationCard from "./NotificationCard";
import PageTitle from "../layout/PageTitle";

const PAGE_SIZE = 20;

export default function Notifications() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const {
    visible,
    loading,
    error,
    markBroadcastRead,
    dismissBroadcast,
  } = useVisibleNotifications({
    domain: "scribe",
    prune: true,
  });

  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    const id = setInterval(forceUpdate, 60_000);
    return () => clearInterval(id);
  }, []);

  const [markRead] = useMutation(MARK_NOTIFICATION_READ, {
    refetchQueries: ["userNotifications"],
  });

  const [dismiss] = useMutation(DISMISS_NOTIFICATION, {
    refetchQueries: ["userNotifications"],
  });

  const handleMarkRead = (n) => {
    if (n.isBroadcast) {
      markBroadcastRead(n.uuid);
      return Promise.resolve();
    }
    return markRead({ variables: { notificationUuid: n.uuid } });
  };

  const handleDismiss = (n) => {
    if (n.isBroadcast) {
      dismissBroadcast(n.uuid);
      return;
    }
    dismiss({ variables: { notificationUuid: n.uuid } });
  };

  const handleRowClick = (n) => {
    if (!n.readAt) {
      handleMarkRead(n).catch(() => {});
    }
    if (n.actionUrl) {
      if (/^https?:\/\//i.test(n.actionUrl)) {
        window.open(n.actionUrl, "_blank", "noopener,noreferrer");
      } else {
        navigate(n.actionUrl);
      }
      return;
    }
    if (n.noteUuid) {
      navigate(`/edit_note/${n.noteUuid}`);
    }
  };

  const sorted = visible
    .slice()
    .sort(
      (a, b) =>
        new Date(b.activeAt ?? b.createdAt).getTime() -
        new Date(a.activeAt ?? a.createdAt).getTime(),
    );

  const totalPages = Math.max(
    1,
    Math.ceil(sorted.length / PAGE_SIZE),
  );
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const pageItems = sorted.slice(start, start + PAGE_SIZE);

  return (
    <div>
      <PageTitle title="Notifications" />

      {loading && sorted.length === 0 ? (
        <div className="text-secondary py-8 text-center">
          Loading…
        </div>
      ) : error ? (
        <div className="text-red-500 py-8 text-center">
          Failed to load notifications.
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center text-secondary py-16">
          <FontAwesomeIcon icon={faBell} size="2x" className="mb-3" />
          <div>No notifications yet</div>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {pageItems.map((n) => (
              <NotificationCard
                key={n.uuid}
                notification={n}
                onClick={handleRowClick}
                onMarkRead={handleMarkRead}
                onDismiss={handleDismiss}
              />
            ))}
          </div>

          <div className="flex items-center justify-between mt-4">
            <DSButton
              variant="secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              dataTestId="notifications-prev"
            >
              Previous
            </DSButton>
            <span className="text-sm text-secondary">
              Page {currentPage} of {totalPages}
            </span>
            <DSButton
              variant="secondary"
              onClick={() =>
                setPage((p) => Math.min(totalPages, p + 1))
              }
              disabled={currentPage >= totalPages}
              dataTestId="notifications-next"
            >
              Next
            </DSButton>
          </div>
        </>
      )}
    </div>
  );
}
